import React, { useState } from 'react'
import moment, { Moment } from 'moment'
import { useMutation } from '@apollo/client'
import { Box, Snackbar, TableCell, TableRow, TextField } from '@mui/material'
import { DeleteOutline as DeleteIcon } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import { colors } from '@resiliatech/birdsong'
import { GOALS } from 'Apollo/Queries/Goals'
import UPDATE_ENTRY_GROUP from 'Apollo/Mutations/UpdateEntryGroup'
import { useAppState } from 'Context/AppContext/AppContext'
import { EntryGroup } from 'Generated/graphql-types'
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { sortByGoalDimensionTitle } from '../utils'

type EditChartDataRowProps = {
  group: EntryGroup
  goalId: string
  showBorderBottom: boolean
  setIsChartDataEdited: (value: boolean) => void
}

export const EditChartDataRow = ({
  group,
  goalId,
  setIsChartDataEdited,
  showBorderBottom,
}: EditChartDataRowProps) => {
  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId },
  } = useAppState()
  const [updateEntryGroup] = useMutation(UPDATE_ENTRY_GROUP, {
    refetchQueries: [
      { query: GOALS, variables: { activeOnly: true, goalId, nonprofitOrgId } },
    ],
    awaitRefetchQueries: true,
  })

  const [isEdited, setIsEdited] = useState(false)
  const [editableEntries, setEditableEntries] = useState([...group.Entry])
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  const popToast = (message: string) => {
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  const handleCloseSnackbar = () => {
    setShowSnackbar(false)
    setSnackbarMessage('')
  }

  const [selectedDate, setSelectedDate] = useState<Moment | null>(
    moment.utc(group.reportedAt)
  )

  const handleDateChange = (date: Moment | null) => {
    setSelectedDate(date)
    setIsChartDataEdited(true)
    setIsEdited(true)
  }

  const handleValueChange = (entryId: number, newValue: string) => {
    const targetEntry = group.Entry.find((ent) => ent.id === entryId)
    if (targetEntry && newValue !== '') {
      setIsEdited(true)
      setIsChartDataEdited(true)
      const newEntries = [
        ...editableEntries.filter((ent) => ent.id !== entryId),
        { ...targetEntry, value: parseFloat(newValue) },
      ]

      setEditableEntries(newEntries)
    }
  }

  const handleSaveEdits = async () => {
    const entryInfo = editableEntries.map((item) => ({
      entryId: item.id,
      value: item.value,
    }))
    await updateEntryGroup({
      variables: {
        entryGroupInfo: {
          entryGroupId: group.id,
          reportedAt: selectedDate
            ? moment(selectedDate).toISOString()
            : group.reportedAt,
        },
        entryInfo,
        nonprofitOrgId,
      },
    })
    setIsEdited(false)
    setIsChartDataEdited(false)
    popToast('Data Points Updated')
  }

  const handleDelete = async () => {
    await updateEntryGroup({
      variables: {
        entryGroupInfo: {
          archivedAt: new Date().toISOString(),
          entryGroupId: group.id,
          reportedAt: group.reportedAt,
        },
        entryInfo: [],
        nonprofitOrgId,
      },
    })
    popToast('Data Points Deleted')
  }

  // Sort by category title as Entry array order isn't guaranteed
  const sortedEntries = group.Entry.map((e) => e).sort(sortByGoalDimensionTitle)

  const categoryCells = sortedEntries.map((entry) => {
    const { value } = entry

    return (
      <TableCell key={entry.id}>
        <TextField
          id={`editChartDataEntryGroup${group.id}Entry${entry.id}`}
          variant='outlined'
          size='small'
          defaultValue={value}
          onChange={({ target }) => {
            handleValueChange(entry.id, target.value)
          }}
          required
          sx={{ height: '40px', width: '75px' }}
          type='number'
        />
      </TableCell>
    )
  })

  return (
    <>
      <TableRow
        key={group.id}
        sx={{
          borderBottom: showBorderBottom
            ? '1px solid rgba(224, 224, 224, 1)'
            : 'none',
        }}
      >
        <TableCell>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale='en-us'
          >
            <MobileDatePicker
              value={selectedDate}
              onChange={(newDate): void => {
                handleDateChange(newDate ?? null)
              }}
              slotProps={{
                textField: {
                  InputProps: {
                    placeholder: '',
                  },
                  size: 'small',
                },
              }}
            />
          </LocalizationProvider>
        </TableCell>
        {categoryCells}
        <TableCell>
          <Box justifyContent='flex-start'>
            <CheckIcon
              cursor={isEdited ? 'pointer' : 'not-allowed'}
              sx={{
                color: !isEdited
                  ? colors.interactive.disabledLight
                  : colors.feedback.successDark,
              }}
              onClick={isEdited ? handleSaveEdits : undefined}
            />
            <DeleteIcon
              cursor={isEdited ? 'not-allowed' : 'pointer'}
              sx={{
                color: isEdited
                  ? colors.interactive.disabledLight
                  : colors.feedback.errorDark,
              }}
              onClick={!isEdited ? handleDelete : undefined}
            />
          </Box>
        </TableCell>
      </TableRow>

      <Snackbar
        open={showSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={snackbarMessage}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
      />
    </>
  )
}
